import { Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { createFileRoute, type ErrorComponentProps, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import Logo from "@/assets/wiseline-color.png";
import Page from "@/components/Page";
import { useCompany } from "@/hooks/Companies";
import { OrganizationPicker } from "@/iam/OrganizationPicker";

export const Route = createFileRoute("/_auth")({
  component: AuthRoute,
  errorComponent: ErrorFallback,
});

function ErrorFallback({ error }: ErrorComponentProps) {
  console.error("Error:", error);
  return (
    <div style={{ display: "grid", placeItems: "center", width: "100%" }}>
      <h2>Oisann! Siden kræsjet.</h2>
      <Button onClick={() => location.reload()}>Last inn på nytt.</Button>
    </div>
  );
}

const Login = () => {
  const { signinRedirect, user } = useAuth();
  const cachedEmail = user?.profile?.email;
  const cachedName = user?.profile?.given_name || cachedEmail?.split("@")[0];

  return (
    <Stack
      component={Paper}
      variant="outlined"
      sx={{
        gap: 1,
        p: 2,
        mt: 5,
        maxWidth: 450,
        mx: "auto",
      }}
    >
      <Box alt="Wiseline logo" component="img" src={Logo} sx={{ height: 75, m: "auto" }} />
      <Typography align="center" sx={{ my: 2 }} variant="h1">
        Wiseline - WAI
      </Typography>
      {cachedEmail ? (
        <>
          <Typography align="center" gutterBottom variant="h3">
            Du har blitt logget ut grunnet manglende aktivitet
          </Typography>
          <Button
            color="primary"
            fullWidth
            onClick={() => signinRedirect({ login_hint: cachedEmail })}
            variant="contained"
          >
            Logg inn som {cachedName}
          </Button>
          <Button variant="text" size="small" onClick={() => signinRedirect()}>
            Logg inn med annen konto
          </Button>
        </>
      ) : (
        <>
          <Typography align="center" gutterBottom variant="h3">
            Du er ikke innlogget
          </Typography>
          <Button color="primary" fullWidth onClick={() => signinRedirect()} variant="contained">
            Logg inn
          </Button>
        </>
      )}
    </Stack>
  );
};

const Loader = () => {
  return (
    <Page>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 4,
        }}
      >
        <CircularProgress size={100} />
      </Box>
    </Page>
  );
};

/**
 * Helper function to determine if an error is authentication-related
 */
const isAuthError = (error: Error): boolean => {
  const authErrorPatterns = [
    "Token is not active",
    "token",
    "authentication",
    "unauthorized",
    "unauthenticated",
    "login_required",
    "invalid_token",
    "expired",
  ];

  const errorText = `${error.name} ${error.message}`.toLowerCase();
  return authErrorPatterns.some((pattern) => errorText.includes(pattern.toLowerCase()));
};

/**
 * Protects a route by checking that the user is signed in with OIDC. Redirects to signin if not.
 */
function AuthRoute() {
  const { isAuthenticated, user, error, isLoading } = useAuth();
  const { selectedCompany, isLoading: isCompanyLoading } = useCompany();

  useEffect(() => {
    const companyId = isAuthenticated ? selectedCompany?.company_id : undefined;
    Sentry.setTag("company_id", companyId);
    Sentry.setContext("company", companyId == null ? null : { company_id: companyId });
  }, [isAuthenticated, selectedCompany?.company_id]);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    // If it's an authentication-related error, show the login screen instead of raw error
    if (isAuthError(error)) {
      return <Login />;
    }
    return (
      <Typography>
        Noe gikk galt. {error.name} - {error.message}
      </Typography>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  if (isCompanyLoading) {
    return <Loader />;
  }

  if (!selectedCompany) {
    return (
      <Page>
        <Paper sx={{ p: 2, maxWidth: 600, mx: "auto", mt: "1em" }}>
          <h2>Velg organisasjon</h2>
          <p>
            Hei! {user?.profile?.name || "Bruker"}, du har tilgang til flere organisasjoner,
            vennligst velg hvilke du vil se.
          </p>
          <OrganizationPicker />
        </Paper>
      </Page>
    );
  }

  return <Outlet />;
}
